import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export function SmartAppBanner() {
    const { t } = useTranslation();
    const [isVisible, setIsVisible] = useState(false);
    const [os, setOs] = useState<'ios' | 'android' | 'desktop'>('desktop');

    useEffect(() => {
        // If it's already a native app, do not show the banner
        if (Capacitor.isNativePlatform()) {
            return;
        }

        // Check localStorage dismissal
        const dismissedAt = localStorage.getItem('hideSmartAppBanner');
        if (dismissedAt) {
            const dismissedTime = parseInt(dismissedAt, 10);
            const now = new Date().getTime();
            const twentyFourHours = 24 * 60 * 60 * 1000;
            if (now - dismissedTime < twentyFourHours) {
                return;
            }
        }

        const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

        // Detect mobile OS
        if (/android/i.test(userAgent)) {
            setOs('android');
            setIsVisible(true);
        } else if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
            setOs('ios');
            setIsVisible(true);
        }
    }, []);

    if (!isVisible) return null;

    const handleClose = () => {
        setIsVisible(false);
        localStorage.setItem('hideSmartAppBanner', new Date().getTime().toString());
    };

    const handleAppSwitch = () => {
        // Capture current path and query params
        const currentPath = window.location.pathname;
        const currentSearch = window.location.search;

        // Android Intents often drop query parameters if the path is just "/"
        // Let's transform /?tab=clubs&search=... to /clubs?search=...
        let finalPath = currentPath;
        let finalSearch = currentSearch;

        if (currentPath === '/') {
            const params = new URLSearchParams(currentSearch);
            const tab = params.get('tab');
            if (tab === 'clubs') {
                finalPath = '/clubs';
                params.delete('tab');
                const remainingSearch = params.toString();
                finalSearch = remainingSearch ? `?${remainingSearch}` : '';
            } else if (tab) {
                // If it's another tab like /?tab=matches, we can just send it as /matches
                // Dashboard currently only handles this explicitly for clubs via App.tsx alias,
                // but this makes the intent safer. For now, leave others as they are, 
                // since the user's main issue was clubs. We can easily add others if needed.
            }
        }

        const fullPath = `${finalPath}${finalSearch}`;
        const universalLink = `https://app.padelo.fr${fullPath}`;

        if (os === 'ios') {
            const appStoreUrl = 'https://apps.apple.com/fr/app/pad-elo/id6758855417';

            // Use hidden iframe to attempt custom URL scheme silently (no Safari error dialog)
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = `padelo://app${fullPath}`;
            document.body.appendChild(iframe);
            setTimeout(() => document.body.removeChild(iframe), 3000);

            // Fallback to App Store if app didn't open
            setTimeout(() => {
                if (!document.hidden) {
                    window.location.href = appStoreUrl;
                }
            }, 2000);
        } else if (os === 'android') {
            const packageId = 'com.padelo.app';
            const playStoreUrl = `https://play.google.com/store/apps/details?id=${packageId}&pcampaignid=web_share`;

            // Use intent:// to bypass browser prompt blocking, but with our cleaned up fullPath
            // Android intent parser is notoriously bad with standard query strings on the root path
            const intentUrl = `intent://app${fullPath}#Intent;scheme=padelo;package=${packageId};action=android.intent.action.VIEW;category=android.intent.category.DEFAULT;category=android.intent.category.BROWSABLE;end`;

            // Try to open Intent
            window.location.href = intentUrl;

            // Fallback to Play Store after delay
            setTimeout(() => {
                if (!document.hidden) {
                    window.location.href = playStoreUrl;
                }
            }, 2000);
        }
    };

    return (
        <div className="sticky top-0 z-50 flex items-center justify-between px-4 py-3 bg-white shadow-md border-b">
            <div className="flex items-center gap-3">
                <button onClick={handleClose} className="p-1 text-gray-500 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0">
                    <X className="w-5 h-5" />
                </button>
                <div className="w-10 h-10 bg-white border border-black rounded-[10px] flex items-center justify-center overflow-hidden flex-shrink-0">
                    <img src="/logo_v2.png" alt="PadELO Logo" className="w-full h-full object-cover" />
                </div>
                <div className="flex flex-col">
                    <span className="text-sm font-semibold leading-tight text-gray-900">PadELO</span>
                    <span className="text-xs text-gray-500 leading-tight">{t('smartAppBanner.description')}</span>
                </div>
            </div>
            <Button
                onClick={handleAppSwitch}
                size="sm"
                className="rounded-full bg-blue-600 hover:bg-blue-700 text-white px-5 h-8 text-xs font-semibold flex-shrink-0"
            >
                {t('smartAppBanner.open')}
            </Button>
        </div>
    );
}
